import React from "react";
import { useTranslation } from "react-i18next";

/**
 * v0.6.12 — a block that says what it is.
 *
 * The operator's words: "I need to see each block clearly and it must suggest
 * what it's about." The first attempt at that was a surface-elevation ladder —
 * three tones about 8% apart — which is the standard enterprise convention and
 * the wrong tool twice over: at these dark values the steps are nearly
 * invisible, and a tone cannot tell anyone what a block is FOR. Only a label
 * can.
 *
 * So every block gets three things:
 *
 *   an ICON       a shape you learn to recognise before you read anything
 *   a TITLE       which names the block's JOB, not its data. "Which period to
 *                 calculate", not "Filing period" — the first tells the
 *                 operator what they are being asked to decide, the second
 *                 just names a field.
 *   a PURPOSE     one line on what you do here, for the times the title is
 *                 not enough on its own.
 *
 * The header carries its own background and a divider, so a block's identity
 * is visually separate from its contents rather than being the first line of
 * them.
 *
 * Icons come from Phosphor, which the app already uses — not emoji, which
 * would read as wrong beside the rest of this tool.
 */
// A2-467 (2026-10-21) — `step` and `accent` make a block one lettered part of
// a page ("Part B": letters, because the wizard's own steps are numbered), in
// that part's own hue (index.css, --accent-*), so neighbouring
// steps stop reading as shades of one colour. `flush` drops the body padding
// for contents that already lay out their own full-width rows.
export const BlockCard = ({
  icon: Icon, title, purpose, testid, right, className = "", children,
  step = null, accent = null, flush = false,
}) => {
  const { t } = useTranslation();
  return (
    <section data-testid={testid} data-accent={accent || undefined} className={`block-card ${className}`}>
      <header className="block-card-head">
        {Icon && (
          <span className="block-card-icon" aria-hidden="true">
            <Icon size={16} weight="bold" />
          </span>
        )}
        <div className="min-w-0">
          {step != null && (
            <div className="block-card-step">{t("ui.block_card.step", { n: step, defaultValue: `Part ${step}` })}</div>
          )}
          <h2 className="block-card-title">{title}</h2>   {/* A2-474: under the page's h1 */}
          {purpose && <div className="block-card-purpose">{purpose}</div>}
        </div>
        {right && <div className="block-card-right">{right}</div>}
      </header>
      <div className={`block-card-body${flush ? " is-flush" : ""}`}>{children}</div>
    </section>
  );
};

export default BlockCard;
